import {
  COLUMN_TYPES,
  COMMON_PLACEHOLDERS,
  USER_PLACEHOLDER_PARTS,
  defaultTitlePattern,
  TITLE_PLACEHOLDERS,
  titleFields,
  MONEY_COMPUTED_FIELDS,
  PROPIS_SUFFIX,
  FIELD_TYPE_LABELS,
  FIELD_TYPES,
  TEMPLATE_WHEN,
  type ColumnType,
  type FieldDefinition,
  type FieldType,
  type RequestTypeDto,
  type RouteDto,
  type Condition,
  type TemplateDto,
  type TemplateUse,
  type TemplateWhen,
} from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { IconPicker, TypeIcon } from '../components/type-icon';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { useToast } from '../lib/ui';

const MAP: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

/** «Сумма договора» → «summaDogovora»: ключ для меток шаблона и условий маршрута. */
export function toKey(label: string): string {
  const words = label
    .toLowerCase()
    .split('')
    .map((c) => MAP[c] ?? c)
    .join('')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  const key = words.map((w, i) => (i === 0 ? w : w[0]!.toUpperCase() + w.slice(1))).join('');
  return /^[a-z]/.test(key) ? key.slice(0, 60) : `f${key}`.slice(0, 60);
}

const WHEN_LABELS: Record<TemplateWhen, string> = {
  manual: 'по кнопке в задаче',
  on_save: 'при сохранении черновика',
  on_approved: 'автоматически после согласования',
};

export function RequestTypesPage() {
  const types = useQuery({ queryKey: ['request-types', 'all'], queryFn: () => api.get<RequestTypeDto[]>('/request-types', { all: true }) });
  const [editing, setEditing] = useState<RequestTypeDto | 'new' | null>(null);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Типы заявок</h1>
          <p className="muted">Поля, обязательность, маршрут и шаблоны документов — без программиста</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>
          <Icon name="plus" size={18} /> Новый тип
        </button>
      </div>
      <div className="route-cards">
        {types.data?.map((t) => (
          <button key={t.id} className="card route-card" onClick={() => setEditing(t)}>
            <div className="section-head">
              <b className="type-title">
                <TypeIcon icon={t.icon} className="type-icon type-icon-sm" />
                {t.name}
              </b>
              <span style={{ display: 'flex', gap: 6 }}>
                {t.system && <span className="badge">встроенный</span>}
                {!t.isActive && <span className="badge badge-danger">отключён</span>}
              </span>
            </div>
            {t.description && <p className="faint">{t.description}</p>}
            <span className="faint">
              Полей: {t.fields.length} · обязательных: {t.fields.filter((f) => f.required).length} · шаблонов: {t.templates.length}
            </span>
          </button>
        ))}
      </div>
      {editing && <TypeEditor type={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function TypeEditor({ type, onClose }: { type: RequestTypeDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const routes = useQuery({ queryKey: ['routes', 'all'], queryFn: () => api.get<RouteDto[]>('/routes', { all: true }) });
  const templates = useQuery({ queryKey: ['templates', 'all'], queryFn: () => api.get<TemplateDto[]>('/templates', { all: true }) });
  const [name, setName] = useState(type?.name ?? '');
  const [key, setKey] = useState(type?.key ?? '');
  const [description, setDescription] = useState(type?.description ?? '');
  const [icon, setIcon] = useState<string | null>(type?.icon ?? null);
  const [titleTemplate, setTitleTemplate] = useState(type?.titleTemplate ?? '');
  const [isActive, setIsActive] = useState(type?.isActive ?? true);
  const [defaultRouteId, setDefaultRouteId] = useState(type?.defaultRouteId ?? '');
  const [allowRouteChoice, setAllowRouteChoice] = useState(type?.allowRouteChoice ?? true);
  const [allowCustomRoute, setAllowCustomRoute] = useState(type?.allowCustomRoute ?? false);
  const [routeIds, setRouteIds] = useState<string[]>(type?.routeIds ?? []);
  const [fields, setFields] = useState<FieldDefinition[]>(type?.fields ?? []);
  const [tpls, setTpls] = useState<TemplateUse[]>(type?.templates ?? []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: () => {
      const body = { key: key || toKey(name), name, description: description || null, icon, titleTemplate: titleTemplate.trim() || null, isActive, defaultRouteId: defaultRouteId || null, allowRouteChoice, allowCustomRoute, routeIds, fields, templates: tpls };
      return type ? api.patch(`/request-types/${type.id}`, body) : api.post('/request-types', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['request-types'] });
      toast('Тип заявки сохранён');
      onClose();
    },
  });
  const err = save.error instanceof ApiError ? save.error : null;
  const { can } = useAuth();
  const remove = useMutation({
    mutationFn: () => api.delete(`/request-types/${type!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['request-types'] });
      toast('Тип заявки удалён');
      onClose();
    },
    onError: (e) => toast(e.message, { error: true }),
  });

  const setField = (i: number, patch: Partial<FieldDefinition>) => setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, d: number) =>
    setFields((fs) => {
      const next = [...fs];
      const [f] = next.splice(i, 1);
      next.splice(i + d, 0, f!);
      return next;
    });

  /** Поля для меток шаблона, которых ещё нет в типе. */
  const fieldsFromTemplate = async (templateId: string) => {
    const check = await api.get<{ unknown: string[] }>(`/templates/${templateId}/check`, { requestTypeId: type?.id });
    const existing = new Set(fields.map((f) => f.key));
    const added = check.unknown.filter((k) => /^[a-z][a-zA-Z0-9_]*$/.test(k) && !existing.has(k));
    if (added.length === 0) {
      toast('Все метки шаблона уже есть среди полей');
      return;
    }
    setFields((fs) => [...fs, ...added.map((k) => ({ key: k, label: k, type: 'text' as const, required: true }))]);
    toast(`Добавлено полей: ${added.length}. Задайте им понятные названия и вид`);
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <form
        className="drawer drawer-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Тип заявки"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <header>
          <h2>{type ? type.name : 'Новый тип заявки'}</h2>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" />
          </button>
        </header>
        <div className="body">
          {err && (
            <div className="alert">
              {err.message}
              {Object.entries(err.fields).slice(0, 6).map(([k, v]) => (
                <div key={k} className="faint">
                  {k}: {v}
                </div>
              ))}
            </div>
          )}
          <div className="two-cols">
            <label className="field">
              <span>Название</span>
              <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span>Ключ (латиница)</span>
              <input className="input" value={key} placeholder={toKey(name)} disabled={type?.system} onChange={(e) => setKey(e.target.value)} />
            </label>
          </div>
          <label className="field">
            <span>Описание (видно при создании)</span>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <div className="field">
            <span>Название документа</span>
            <input
              className="input mono"
              value={titleTemplate}
              maxLength={300}
              placeholder={defaultTitlePattern(fields)}
              onChange={(e) => setTitleTemplate(e.target.value)}
              aria-label="Шаблон названия"
            />
            <span className="faint">
              Название новой заявки собирается само; автор может его исправить. Пусто — «{defaultTitlePattern(fields)}». Нажмите, чтобы добавить:
            </span>
            <div className="title-chips">
              {[...TITLE_PLACEHOLDERS, ...titleFields(fields).map((f) => ({ key: f.key, label: f.label }))].map((p) => (
                <button key={p.key} type="button" className="chip" onClick={() => setTitleTemplate((v) => `${v}${v && !v.endsWith(' ') ? ' ' : ''}{{${p.key}}}`)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <span>Иконка (на экране «Что создаём?»)</span>
            <IconPicker value={icon} onChange={setIcon} />
            {err?.fields.icon && <span className="field-error">{err.fields.icon}</span>}
          </div>
          <div className="two-cols">
            <label className="field">
              <span>Маршрут по умолчанию</span>
              <select className="select" value={defaultRouteId} onChange={(e) => setDefaultRouteId(e.target.value)}>
                <option value="">—</option>
                {routes.data?.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="check" style={{ alignSelf: 'end' }}>
              <input type="checkbox" checked={allowRouteChoice} onChange={(e) => setAllowRouteChoice(e.target.checked)} />
              <span>Автор может выбрать другой маршрут</span>
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={allowCustomRoute} onChange={(e) => setAllowCustomRoute(e.target.checked)} />
            <span>Разрешить свой маршрут — автор сам собирает этапы и выбирает конкретных сотрудников (только для этого документа)</span>
          </label>
          {allowRouteChoice && (
            <div className="field">
              <span>Маршруты для выбора</span>
              <div className="check-list">
                {routes.data
                  ?.filter((r) => r.isActive)
                  .map((r) => (
                    <label key={r.id} className="check">
                      <input
                        type="checkbox"
                        checked={routeIds.includes(r.id)}
                        onChange={(e) => setRouteIds((ids) => (e.target.checked ? [...ids, r.id] : ids.filter((x) => x !== r.id)))}
                      />
                      <span>{r.name}</span>
                    </label>
                  ))}
              </div>
              <span className="faint">
                Автор выбирает только из отмеченных. Отмеченные маршруты не предлагаются в других типах, где маршруты не отмечены (например, маршрут командировки — в
                договорах). Ничего не отмечено — доступны маршруты, не закреплённые за другими типами.
              </span>
            </div>
          )}
          <label className="check">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span>Доступен для новых заявок</span>
          </label>

          <fieldset>
            <legend>Поля</legend>
            {fields.length === 0 && <span className="faint">Полей нет — будут только название и описание</span>}
            {fields.map((f, i) => (
              <FieldEditor
                key={i}
                field={f}
                onChange={(patch) => setField(i, patch)}
                onRemove={() => setFields((fs) => fs.filter((_, j) => j !== i))}
                onMove={(d) => move(i, d)}
                first={i === 0}
                last={i === fields.length - 1}
                others={fields.filter((x, j) => j !== i && x.key)}
              />
            ))}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFields((fs) => [...fs, { key: '', label: '', type: 'text', required: false }])}>
              + поле
            </button>
            {type && type.computed.length > 0 && (
              <p className="faint">Считает система: {type.computed.map((c) => `${c.label} ({{${c.key}}})`).join(', ')}</p>
            )}
          </fieldset>

          <fieldset>
            <legend>Шаблоны документов</legend>
            {tpls.map((t, i) => (
              <div key={i} className="template-use">
              <div className="step-edit-row">
                <select className="select" value={t.templateId} onChange={(e) => setTpls((xs) => xs.map((x, j) => (j === i ? { ...x, templateId: e.target.value } : x)))} aria-label="Шаблон">
                  {templates.data?.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name} (.{tpl.format})
                    </option>
                  ))}
                </select>
                <select className="select" value={t.when} onChange={(e) => setTpls((xs) => xs.map((x, j) => (j === i ? { ...x, when: e.target.value as TemplateWhen } : x)))} aria-label="Когда">
                  {TEMPLATE_WHEN.map((w) => (
                    <option key={w} value={w}>
                      {WHEN_LABELS[w]}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-ghost btn-sm" title="Добавить поля по меткам шаблона" onClick={() => void fieldsFromTemplate(t.templateId)}>
                  поля из меток
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTpls((xs) => xs.filter((_, j) => j !== i))} aria-label="Убрать шаблон">
                  ×
                </button>
              </div>
              <TemplateCondition
                fields={fields}
                value={t.condition}
                onChange={(condition) => setTpls((xs) => xs.map((x, j) => (j === i ? { ...x, condition } : x)))}
              />
              </div>
            ))}
            {tpls.some((t) => t.condition) && (
              <p className="faint">Шаблон с условием формируется и предлагается в задаче, только если условие выполнено. Так в одном типе заявки — разные договоры для разных контрагентов.</p>
            )}
            {templates.data && templates.data.length > 0 ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTpls((xs) => [...xs, { templateId: templates.data![0]!.id, when: 'manual' }])}>
                + шаблон
              </button>
            ) : (
              <span className="faint">Сначала загрузите шаблон в разделе «Шаблоны»</span>
            )}
            <details>
              <summary className="faint">Какие метки можно использовать в шаблонах</summary>
              <ul className="placeholder-list">
                {[...COMMON_PLACEHOLDERS, ...fields.map((f) => ({ key: f.key, label: f.label })), ...(type?.computed ?? [])].map((p) => (
                  <li key={p.key}>
                    <code>{`{{${p.key}}}`}</code> — {p.label}
                  </li>
                ))}
                {fields
                  .filter((f) => f.type === 'user')
                  .flatMap((f) =>
                    USER_PLACEHOLDER_PARTS.map((part) => (
                      <li key={f.key + part.suffix}>
                        <code>{`{{${f.key}${part.suffix}}}`}</code> — {f.label.toLowerCase()}: {part.label}
                      </li>
                    )),
                  )}
                {[
                  ...fields.filter((f) => f.type === 'money').map((f) => ({ key: f.key, label: f.label })),
                  ...fields.flatMap((f) => (f.type === 'table' ? (f.columns ?? []).filter((c) => c.type === 'money').map((c) => ({ key: c.key, label: c.label })) : [])),
                  ...(type?.computed ?? []).filter((c) => MONEY_COMPUTED_FIELDS.has(c.key)),
                ].map((p) => (
                  <li key={p.key + PROPIS_SUFFIX}>
                    <code>{`{{${p.key}${PROPIS_SUFFIX}}}`}</code> — {p.label.toLowerCase()} прописью: «Сто десять рублей 50 копеек»
                  </li>
                ))}
                {fields
                  .filter((f) => f.type === 'table')
                  .map((f) => (
                    <li key={`${f.key}-loop`}>
                      <code>{`{{#${f.key}}} ${(f.columns ?? []).map((c) => `{{${c.key}}}`).join(' ')} {{/${f.key}}}`}</code> — строки таблицы «{f.label}»
                    </li>
                  ))}
              </ul>
            </details>
          </fieldset>
        </div>
        <footer>
          {type && !type.system && can('records.delete') && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginRight: 'auto', color: 'var(--danger)' }}
              disabled={remove.isPending}
              onClick={() =>
                window.confirm(
                  `Удалить тип заявки «${type.name}» навсегда?\n\nУдалить можно только тип, по которому нет ни одного документа (и черновика). Отменить нельзя — если тип просто больше не нужен, снимите галочку «Активен».`,
                ) && remove.mutate()
              }
            >
              Удалить навсегда
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button className="btn btn-primary" disabled={save.isPending || !name.trim()}>
            Сохранить
          </button>
        </footer>
      </form>
    </>
  );
}

function FieldEditor({
  field,
  onChange,
  onRemove,
  onMove,
  first,
  last,
  others,
}: {
  field: FieldDefinition;
  others: FieldDefinition[];
  onChange: (p: Partial<FieldDefinition>) => void;
  onRemove: () => void;
  onMove: (d: number) => void;
  first: boolean;
  last: boolean;
}) {
  const [keyTouched, setKeyTouched] = useState(Boolean(field.key));
  return (
    <div className="step-edit">
      <div className="field-edit-row">
        <input
          className="input"
          placeholder="Название поля"
          value={field.label}
          onChange={(e) => onChange({ label: e.target.value, ...(!keyTouched && !field.system ? { key: toKey(e.target.value) } : {}) })}
          aria-label="Название поля"
        />
        <select className="select" value={field.type} disabled={field.system} onChange={(e) => onChange({ type: e.target.value as FieldType })} aria-label="Вид поля">
          {FIELD_TYPES.map((t) => (
            <option key={t} value={t}>
              {FIELD_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <label className="check">
          <input type="checkbox" checked={field.required} onChange={(e) => onChange({ required: e.target.checked })} />
          <span>обязательное</span>
        </label>
        {!field.system && (
          <label className="check" title="Поле скрыто от автора; его заполняет участник на шаге маршрута с «Заполнить поля»">
            <input type="checkbox" checked={Boolean(field.byApprover)} onChange={(e) => onChange({ byApprover: e.target.checked || undefined })} />
            <span>заполняет согласующий</span>
          </label>
        )}
        <span className="stage-tools">
          <button type="button" className="btn btn-ghost btn-sm" disabled={first} onClick={() => onMove(-1)} aria-label="Выше">
            ↑
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={last} onClick={() => onMove(1)} aria-label="Ниже">
            ↓
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={field.system} onClick={onRemove} aria-label="Удалить поле" title={field.system ? 'Системное поле нельзя удалить' : undefined}>
            ×
          </button>
        </span>
      </div>
      <div className="field-edit-row">
        <input
          className="input mono"
          placeholder="ключ"
          value={field.key}
          disabled={field.system}
          onChange={(e) => {
            setKeyTouched(true);
            onChange({ key: e.target.value });
          }}
          aria-label="Ключ поля"
        />
        <input className="input" placeholder="Подсказка (необязательно)" value={field.hint ?? ''} onChange={(e) => onChange({ hint: e.target.value || undefined })} aria-label="Подсказка" />
      </div>
      {field.type === 'user' && field.key && (
        <span className="faint">
          В шаблоне: <code>{`{{${field.key}}}`}</code> — ФИО
          {USER_PLACEHOLDER_PARTS.map((part) => (
            <span key={part.suffix}>
              , <code>{`{{${field.key}${part.suffix}}}`}</code> — {part.label}
            </span>
          ))}
        </span>
      )}
      {field.type === 'select' && (
        <textarea
          className="textarea"
          rows={3}
          placeholder="Варианты — по одному в строке"
          value={(field.options ?? []).join('\n')}
          onChange={(e) => onChange({ options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
          aria-label="Варианты"
        />
      )}
      {field.type === 'document' && <DocumentTypesPick value={field.documentTypes ?? []} onChange={(v) => onChange({ documentTypes: v.length ? v : undefined })} />}
      {!field.system && (
        <TemplateCondition
          fields={others}
          value={field.showIf}
          onChange={(showIf) => onChange({ showIf })}
          label="показывать, только если"
          addLabel="+ показывать, только если…"
          addTitle="Например, паспорт — только если «Тип контрагента» = ФЛ. Пока условие не выполнено, поле скрыто, не обязательно и не сохраняется"
        />
      )}
      {field.type === 'table' && (
        <div className="columns-edit">
          {(field.columns ?? []).map((c, i) => (
            <span key={i} className="step-edit-row">
              <input
                className="input"
                value={c.label}
                placeholder="Колонка"
                disabled={field.system}
                onChange={(e) => onChange({ columns: field.columns!.map((x, j) => (j === i ? { ...x, label: e.target.value, key: toKey(e.target.value) } : x)) })}
                aria-label="Колонка"
              />
              <select
                className="select"
                value={c.type}
                disabled={field.system}
                onChange={(e) => onChange({ columns: field.columns!.map((x, j) => (j === i ? { ...x, type: e.target.value as ColumnType } : x)) })}
                aria-label="Вид колонки"
              >
                {COLUMN_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {FIELD_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </span>
          ))}
          {!field.system && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange({ columns: [...(field.columns ?? []), { key: '', label: '', type: 'text' }] })}>
              + колонка
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type SimpleOp = 'eq' | 'neq' | 'in';
const SIMPLE_OPS: Record<SimpleOp, string> = { eq: 'равно', neq: 'не равно', in: 'одно из' };

/** Из каких видов заявок выбирать связанный документ: для оплаты — только договоры. */
function DocumentTypesPick({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const types = useQuery({ queryKey: ['request-types', 'all'], queryFn: () => api.get<RequestTypeDto[]>('/request-types', { all: true }), staleTime: 60_000 });
  const name = (k: string) => types.data?.find((t) => t.key === k)?.name ?? k;
  return (
    <div className="fill-fields">
      <span className="faint">Выбирать из:</span>
      {value.length === 0 && <span className="faint">любых документов</span>}
      {value.map((k) => (
        <span key={k} className="chip">
          {name(k)}
          <button type="button" className="chip-x" onClick={() => onChange(value.filter((x) => x !== k))} aria-label={`Убрать ${name(k)}`}>
            ×
          </button>
        </span>
      ))}
      <select className="select select-inline" value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])} aria-label="Вид заявки для выбора">
        <option value="">+ вид заявки</option>
        {types.data
          ?.filter((t) => !value.includes(t.key))
          .map((t) => (
            <option key={t.key} value={t.key}>
              {t.name}
            </option>
          ))}
      </select>
    </div>
  );
}

/** «Только если поле … равно …» — условие шаблона или видимости поля по полю этого типа заявки. */
function TemplateCondition({
  fields,
  value,
  onChange,
  label = 'только если',
  addLabel = '+ только если…',
  addTitle,
}: {
  fields: FieldDefinition[];
  value: Condition | undefined;
  onChange: (c: Condition | undefined) => void;
  label?: string;
  addLabel?: string;
  addTitle?: string;
}) {
  const usable = fields.filter((f) => !['table', 'user', 'department', 'document'].includes(f.type));
  const rule = value && 'field' in value && ['eq', 'neq', 'in'].includes(value.op) ? value : null;
  if (value && !rule) {
    return (
      <div className="template-cond faint">
        Сложное условие.{' '}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(undefined)}>
          Сбросить
        </button>
      </div>
    );
  }
  if (!rule) {
    return (
      <div className="template-cond">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          title={addTitle}
          disabled={usable.length === 0}
          onClick={() => {
            // Удобнее начинать со списка («Тип контрагента»), если он есть.
            const first = usable.find((f) => f.type === 'select') ?? usable[0]!;
            onChange({ field: first.key, op: 'eq', value: first.options?.[0] ?? '' });
          }}
        >
          {addLabel}
        </button>
        {usable.length === 0 && <span className="faint">добавьте поле, например список «Тип контрагента»</span>}
      </div>
    );
  }
  const field = fields.find((f) => f.key === rule.field);
  const op = rule.op as SimpleOp;
  const list = Array.isArray(rule.value) ? rule.value.map(String) : [];
  const single = typeof rule.value === 'string' || typeof rule.value === 'number' ? String(rule.value) : '';
  const parse = (raw: string) => (field?.type === 'number' || field?.type === 'money' ? Number(raw.replace(',', '.')) : field?.type === 'checkbox' ? raw === 'true' : raw);
  const set = (patch: { op?: SimpleOp; value: unknown }) => onChange({ ...rule, ...patch } as Condition);

  return (
    <div className="template-cond">
      <span className="faint">{label}</span>
      <select
        className="select"
        value={rule.field}
        aria-label="Поле условия"
        onChange={(e) => {
          const f = fields.find((x) => x.key === e.target.value);
          onChange({ field: e.target.value, op, value: op === 'in' ? [] : (f?.options?.[0] ?? '') } as Condition);
        }}
      >
        {!field && <option value={rule.field}>{rule.field}</option>}
        {usable.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>
      <select
        className="select"
        value={op}
        aria-label="Сравнение"
        onChange={(e) => {
          const next = e.target.value as SimpleOp;
          set({ op: next, value: next === 'in' ? (single ? [single] : []) : (list[0] ?? single) });
        }}
      >
        {(Object.keys(SIMPLE_OPS) as SimpleOp[]).map((o) => (
          <option key={o} value={o}>
            {SIMPLE_OPS[o]}
          </option>
        ))}
      </select>
      {op === 'in' ? (
        field?.type === 'select' ? (
          <span className="template-cond-options">
            {(field.options ?? []).map((o) => (
              <label key={o} className="check-inline">
                <input type="checkbox" checked={list.includes(o)} onChange={(e) => set({ value: e.target.checked ? [...list, o] : list.filter((x) => x !== o) })} />
                {o}
              </label>
            ))}
          </span>
        ) : (
          <input className="input" placeholder="значения через ;" defaultValue={list.join('; ')} onBlur={(e) => set({ value: e.target.value.split(';').map((x) => x.trim()).filter(Boolean) })} aria-label="Значения" />
        )
      ) : field?.type === 'select' ? (
        <select className="select" value={single} onChange={(e) => set({ value: e.target.value })} aria-label="Значение">
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : field?.type === 'checkbox' ? (
        <select className="select" value={String(rule.value === true)} onChange={(e) => set({ value: e.target.value === 'true' })} aria-label="Значение">
          <option value="true">отмечено</option>
          <option value="false">не отмечено</option>
        </select>
      ) : (
        <input className="input" defaultValue={single} onBlur={(e) => set({ value: parse(e.target.value) })} aria-label="Значение" />
      )}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(undefined)} aria-label="Убрать условие">
        ×
      </button>
    </div>
  );
}
